/**
 * The cloud productions page (container, PLAN §10.2 screen 5, D11, D25): the
 * table of cloud productions (polled while one is on the move), the launch
 * dialog with its cost estimate and the egress IPs to allowlist, and stop.
 * Launching and stopping are offered per the user's grants
 * (`production.launch.cloud`, D14). A launched production is highlighted in
 * the table; its playout view is the ordinary one (`/productions/$id`).
 */
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CloudProductionsTable, playoutHref } from '../components/cloudProductions/CloudProductionsTable.js';
import { LaunchCloudProductionDialog } from '../components/cloudProductions/LaunchCloudProductionDialog.js';
import { useCloudProductions, useGrants, useLaunchCloudProduction, usePlaylists, useRundowns, useShowStyles, useStopCloudProduction, useStudios } from '../app/hooks.js';
import { cloudProductionRow } from '../lib/cloudProductions.js';
import { allows, allowsSomewhere } from '../lib/permissions.js';
import { sourceLabel } from './ProductionsView.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function CloudProductionsView() {
  const cloud = useCloudProductions();
  const studios = useStudios();
  const showStyles = useShowStyles();
  const rundowns = useRundowns();
  const playlists = usePlaylists();
  const grants = useGrants();
  const launch = useLaunchCloudProduction();
  const stop = useStopCloudProduction();
  const navigate = useNavigate();
  const [launching, setLaunching] = useState(false);
  const [launchedId, setLaunchedId] = useState<string | null>(null);

  const names = useMemo(
    () => ({
      studios: new Map((studios.data ?? []).map((s) => [s.studioId, s.name])),
      rundowns: new Map((rundowns.data ?? []).map((r) => [r.rundownId, r.name])),
      playlists: new Map((playlists.data ?? []).map((p) => [p.playlistId, p.name])),
    }),
    [studios.data, rundowns.data, playlists.data],
  );
  const network = cloud.data?.network ?? null;
  const rows = (cloud.data?.cloudProductions ?? []).map((p) =>
    cloudProductionRow(p, {
      studioName: names.studios.get(p.studioId) ?? p.studioId,
      source: sourceLabel(p, names.rundowns, names.playlists),
      mayStop: allows(grants, 'production.launch.cloud', { siteId: p.siteId, studioId: p.studioId }),
      formatTime: (iso) => new Date(iso).toLocaleString(),
    }),
  );

  return (
    <div className="mx-auto grid max-w-7xl gap-6">
      <CloudProductionsTable
        rows={rows}
        loading={cloud.isFetching}
        error={cloud.error === null ? null : messageOf(cloud.error)}
        egressIps={network?.egressIps ?? []}
        region={network?.region ?? null}
        canLaunch={allowsSomewhere(grants, 'production.launch.cloud')}
        highlightId={launchedId}
        onLaunch={() => {
          launch.reset();
          setLaunching(true);
        }}
        onRetry={() => {
          void cloud.refetch();
        }}
        onOpen={(productionId) => {
          void navigate({ to: playoutHref(productionId) });
        }}
        onStop={(productionId, reason) => {
          stop.mutate(
            { productionId, reason },
            {
              onSuccess: () => toast.success('Stopping the cloud production'),
              onError: (err) => toast.error(`Could not stop it: ${messageOf(err)}`),
            },
          );
        }}
      />
      <LaunchCloudProductionDialog
        open={launching}
        studios={(studios.data ?? []).map((s) => ({ id: s.studioId, name: s.name, siteId: s.siteId, defaultShowStyleId: s.defaultShowStyleId }))}
        showStyles={(showStyles.data ?? []).map((s) => ({ id: s.showStyleId, name: s.name }))}
        rundowns={(rundowns.data ?? []).map((r) => ({ id: r.rundownId, name: r.name }))}
        playlists={(playlists.data ?? []).map((p) => ({ id: p.playlistId, name: p.name }))}
        sizes={network?.sizes ?? []}
        egressIps={network?.egressIps ?? []}
        region={network?.region ?? null}
        pending={launch.isPending}
        error={launch.error === null ? null : messageOf(launch.error)}
        onCancel={() => setLaunching(false)}
        onLaunch={(request) => {
          launch.mutate(request, {
            onSuccess: (launched) => {
              setLaunching(false);
              setLaunchedId(launched.productionId);
              toast.success(launched.startAt === null ? 'Cloud production launching — ready in about two minutes' : 'Cloud production scheduled');
            },
          });
        }}
      />
    </div>
  );
}
